import InspectLink from '../../components/InspectLink';
import Table from '../../components/Table';

import { Absent } from './AccountFields';
import { NameOrId } from './AccountLinks';
import { formatOwnership, formatResidenceType, formatVehicle } from './accountsView';

import type { AdminGetFamilyAccountQuery } from './__generated__/AccountsScene.gql';

type Possessions = NonNullable<AdminGetFamilyAccountQuery['adminGetFamilyAccount']>['possessions'];

interface IPossessionsPanelProps {
  possessions: Possessions;
}

/**
 * The household's residences and vehicles — the only two possession tables.
 *
 * Tabular rather than blocked, unlike the panels above: a possession carries a
 * handful of short attributes and a household can have several, so the columns
 * line up and read down.
 */
const PossessionsPanel = ({ possessions }: IPossessionsPanelProps) => {
  const total = possessions.residences.length + possessions.vehicles.length;

  return (
    <section className="flex min-w-0 flex-col gap-3">
      <h2 className="border-border-primary text-fg-primary flex items-baseline gap-2 border-b pb-2 text-sm font-semibold uppercase tracking-widest">
        Possessions
        <span className="text-fg-tertiary text-xs font-normal normal-case tracking-normal">{total}</span>
      </h2>

      {total === 0 ? (
        <div className="text-fg-tertiary text-xs italic">No residences or vehicles on record.</div>
      ) : (
        <div className="flex flex-col gap-4">
          {possessions.residences.length > 0 && (
            <div className="flex flex-col gap-1">
              <h3 className="text-fg-tertiary text-xs uppercase tracking-wider">Residences</h3>
              <Table className="table-fixed">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Address</Table.HeaderCell>
                    <Table.HeaderCell className="w-[12rem]">Kind</Table.HeaderCell>
                    <Table.HeaderCell className="w-[10rem]">Owner</Table.HeaderCell>
                    <Table.HeaderCell className="w-[8rem]">Held</Table.HeaderCell>
                    <Table.HeaderCell className="w-[5.5rem]">Id</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {possessions.residences.map((residence) => (
                    <Table.Row key={residence.id}>
                      <Table.Cell title={residence.address ?? residence.city}>
                        <div className="flex items-baseline gap-2">
                          <span className="truncate">{residence.address ?? <Absent>{residence.city}</Absent>}</span>
                          {residence.isPrimary && (
                            <span className="text-fg-tertiary shrink-0 text-[10px] uppercase tracking-wide">
                              primary
                            </span>
                          )}
                        </div>
                      </Table.Cell>
                      <Table.Cell muted className="truncate">
                        {formatResidenceType(residence.residenceType, residence.residenceTypeOther)}
                      </Table.Cell>
                      <Table.Cell className="truncate">
                        {residence.owner ? <NameOrId name={residence.owner.name} /> : <Absent>Household</Absent>}
                      </Table.Cell>
                      <Table.Cell muted className="truncate">
                        {formatOwnership(residence.ownership, residence.ownershipOther) ?? '—'}
                      </Table.Cell>
                      <Table.Cell className="text-xs">
                        <InspectLink id={residence.id} short />
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}

          {possessions.vehicles.length > 0 && (
            <div className="flex flex-col gap-1">
              <h3 className="text-fg-tertiary text-xs uppercase tracking-wider">Vehicles</h3>
              <Table className="table-fixed">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Vehicle</Table.HeaderCell>
                    <Table.HeaderCell className="w-[10rem]">Owner</Table.HeaderCell>
                    <Table.HeaderCell className="w-[8rem]">Held</Table.HeaderCell>
                    <Table.HeaderCell className="w-[5.5rem]">Id</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {possessions.vehicles.map((vehicle) => (
                    <Table.Row key={vehicle.id}>
                      <Table.Cell className="truncate">{formatVehicle(vehicle)}</Table.Cell>
                      <Table.Cell className="truncate">
                        {vehicle.owner ? <NameOrId name={vehicle.owner.name} /> : <Absent>Household</Absent>}
                      </Table.Cell>
                      <Table.Cell muted className="truncate">
                        {formatOwnership(vehicle.ownership, vehicle.ownershipOther) ?? '—'}
                      </Table.Cell>
                      <Table.Cell className="text-xs">
                        <InspectLink id={vehicle.id} short />
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default PossessionsPanel;
